// src/pages/ingresos/components/RemitosModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import {
  createRemito,
  listRemitosByOS,
  listRemitoItems,
  addRemitoItem,
  removeRemitoItem,
  deleteRemito, // ✅ ahora borramos por la función correcta
  printRemitoSoloTexto,
  syncDepartureDateForRemito,
  buildCodigoDigits,
} from "../../../utils/api-services";

// ----------------- Helpers fecha (evita -1 día por zona horaria) -----------------
const pad2 = (n) => String(n).padStart(2, "0");

function getTodayLocalYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Convierte YYYY-MM-DD -> ISO al mediodía UTC (evita que al mostrar en AR quede día anterior)
function ymdToNoonIso(ymd) {
  if (!ymd) return null;
  return `${ymd}T12:00:00.000Z`;
}

function toInputYMD(value) {
  if (!value) return "";
  const s = String(value);
  return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
}

function formatEsDate(value) {
  const ymd = toInputYMD(value);
  if (!ymd || ymd.length < 10) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function getCategoryName(instr) {
  return (
    instr?.category_name ||
    instr?.instrument_categories?.name ||
    instr?.category?.name ||
    null
  );
}

function buildInstrumentLabel(instr) {
  if (!instr) return "";
  const cat = getCategoryName(instr);
  const head = cat ? cat : instr?.type ? String(instr.type).toUpperCase() : "";
  const parts = [];
  if (instr.brand) parts.push(`MARCA: ${instr.brand}`);
  if (instr.model) parts.push(`MOD: ${instr.model}`);
  if (instr.serial) parts.push(`S/N: ${instr.serial}`);
  if (instr.tag) parts.push(`CC: ${instr.tag}`);
  const tail = parts.length ? `, ${parts.join(", ")}` : "";
  return `${head}${tail}`.trim();
}

export default function RemitosModal({
  open,
  os,
  instrumentsOfOS: propInstruments = [],
  onClose,
  setToast = () => {},
  canDeleteRemitos = true,
}) {
  const [saving, setSaving] = useState(false);
  const [remitos, setRemitos] = useState([]);
  const [currentRemitoId, setCurrentRemitoId] = useState(null);
  const [remitoItems, setRemitoItems] = useState([]);

  // Edición de remitos
  const [editingRemitoId, setEditingRemitoId] = useState(null);
  const isEditing = Boolean(editingRemitoId);

  const [officialNumber, setOfficialNumber] = useState("0001-0000");
  const [dateOut, setDateOut] = useState(getTodayLocalYMD());
  const [notes, setNotes] = useState("");

  const [calib, setCalib] = useState({ offsetX: 0, offsetY: 0 });
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // ---- Estado de instrumentos (con OS code + category) ----
  const [instrumentsOfOS, setInstrumentsOfOS] = useState(propInstruments);

  // Instrumentos ya remitados en cualquier remito de esta OS (id -> Set(remito_id))
  const [remittedMap, setRemittedMap] = useState(new Map());

  // Estado actual de cada instrumento (id -> status). Para confirmar entrega.
  const [statusById, setStatusById] = useState({});
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);

  const onlyDigits = (str) => {
    if (str === 0) return "0";
    if (!str && str !== 0) return "";
    const m = String(str).match(/\d+/g);
    return m ? m.join("") : "";
  };

  // Enriquecer instrumentos: OS code + category name
  useEffect(() => {
    async function enrichInstruments(baseList) {
      if (!os?.id) return baseList;
      const list = baseList || [];

      // 1) Codes desde service_orders
      let withCodes = list;
      try {
        const { data: osRows, error } = await supabase
          .from("service_orders")
          .select("instrument_id, code, po_id")
          .eq("po_id", os?.po_id || os?.id);
        if (error) throw error;
        const byInstr = new Map((osRows || []).map((r) => [r.instrument_id, r.code]));
        withCodes = list.map((it) => ({
          ...it,
          os_code: it.os_code || it.code || byInstr.get(it.id) || null,
        }));
      } catch (e) {
        console.warn("[RemitosModal] enrich codes:", e?.message || e);
      }

      // 2) Category name desde instruments -> instrument_categories
      try {
        const missingCat = withCodes.some(
          (it) => !it?.category_name && !it?.instrument_categories?.name
        );
        if (!missingCat) return withCodes;

        const ids = withCodes.map((x) => x.id).filter(Boolean);
        if (!ids.length) return withCodes;

        const { data, error } = await supabase
          .from("instruments")
          .select("id, type, category_id, instrument_categories(name)")
          .in("id", ids);
        if (error) throw error;

        const byId = new Map(
          (data || []).map((r) => [
            r.id,
            {
              type: r.type,
              category_name: r.instrument_categories?.name || null,
            },
          ])
        );

        return withCodes.map((it) => {
          const extra = byId.get(it.id);
          if (!extra) return it;
          return {
            ...it,
            type: it.type || extra.type,
            category_name: it.category_name || extra.category_name,
            instrument_categories:
              it.instrument_categories ||
              (extra.category_name ? { name: extra.category_name } : undefined),
          };
        });
      } catch (e) {
        console.warn("[RemitosModal] enrich categories:", e?.message || e);
        return withCodes;
      }
    }

    (async () => {
      const enriched = await enrichInstruments(propInstruments || []);
      setInstrumentsOfOS(enriched || []);
    })();
    // eslint-disable-next-line
  }, [propInstruments, os?.id, open]);

  async function loadRemitosExistentes() {
    if (!os?.id) return [];
    const rows = await listRemitosByOS(os.id, os);
    setRemitos(rows);
    if (!currentRemitoId && rows.length) setCurrentRemitoId(rows[0].id);
    return rows;
  }

  async function refreshItems() {
    if (!currentRemitoId) {
      setRemitoItems([]);
      return;
    }
    const rows = await listRemitoItems(currentRemitoId);
    setRemitoItems(rows);
  }

  // Mapa de “ya remitados” para esta OS
  async function refreshRemittedMap(targetRemitos = null) {
    try {
      const rList = targetRemitos ?? remitos;
      if (!rList?.length) {
        setRemittedMap(new Map());
        return;
      }
      const ids = rList.map((r) => r.id);
      const { data: items, error } = await supabase
        .from("remito_items")
        .select("instrument_id, remito_id")
        .in("remito_id", ids);
      if (error) throw error;

      const map = new Map();
      for (const it of items || []) {
        if (!map.has(it.instrument_id)) map.set(it.instrument_id, new Set());
        map.get(it.instrument_id).add(it.remito_id);
      }
      setRemittedMap(map);
    } catch (e) {
      console.warn("[RemitosModal] refreshRemittedMap:", e?.message || e);
      setRemittedMap(new Map());
    }
  }

  // Cargar el estado (status) de cada instrumento de la OS
  async function loadStatuses() {
    const ids = (instrumentsOfOS || []).map((i) => i.id).filter(Boolean);
    if (!ids.length) {
      setStatusById({});
      return;
    }
    const { data, error } = await supabase
      .from("instruments")
      .select("id, status")
      .in("id", ids);
    if (error) {
      console.warn("[RemitosModal] loadStatuses:", error.message);
      return;
    }
    const m = {};
    for (const r of data || []) m[r.id] = r.status;
    setStatusById(m);
  }

  // Confirmar entrega de TODO un remito (el cliente ya lo recibió)
  async function confirmRemitoDelivery(remito) {
    if (!remito?.id || confirmingDelivery) return;
    const ok = window.confirm(
      `¿Confirmar la ENTREGA del remito ${remito.official_number || remito.code || ""}?\n\n` +
        `Marca como "Entregado" a todos sus instrumentos (los que estén remitados).`
    );
    if (!ok) return;
    try {
      setConfirmingDelivery(true);
      const { data: items, error: itErr } = await supabase
        .from("remito_items")
        .select("instrument_id")
        .eq("remito_id", remito.id);
      if (itErr) throw itErr;
      const ids = (items || []).map((x) => x.instrument_id).filter(Boolean);
      if (!ids.length) {
        setToast({ type: "error", msg: "El remito no tiene instrumentos." });
        return;
      }
      // Solo pasa a 'delivered' lo que esté 'remitted' (no toca rechazados)
      const { error } = await supabase
        .from("instruments")
        .update({ status: "delivered" })
        .in("id", ids)
        .eq("status", "remitted");
      if (error) throw error;
      await loadStatuses();
      setToast({ type: "success", msg: "Entrega confirmada." });
    } catch (e) {
      console.error(e);
      setToast({ type: "error", msg: e.message || "No se pudo confirmar la entrega." });
    } finally {
      setConfirmingDelivery(false);
    }
  }

  // Confirmar / revertir entrega de UN instrumento
  async function toggleInstrumentDelivery(instrumentId, deliver) {
    if (!instrumentId) return;
    let q = supabase.from("instruments");
    if (deliver) {
      // Entregar: desde cualquier estado salvo rechazado o ya entregado
      q = q
        .update({ status: "delivered" })
        .eq("id", instrumentId)
        .neq("status", "rejected")
        .neq("status", "delivered");
    } else {
      // Deshacer entrega: vuelve a remitido
      q = q.update({ status: "remitted" }).eq("id", instrumentId).eq("status", "delivered");
    }
    const { error } = await q;
    if (error) {
      setToast({ type: "error", msg: error.message || "No se pudo actualizar la entrega." });
      return;
    }
    await loadStatuses();
  }

  // Devuelve cuántos instrumentos del remito están entregados / remitados
  function remitoDeliveryState(remitoId) {
    let entregados = 0;
    let total = 0;
    for (const [instrId, set] of remittedMap.entries()) {
      if (set?.has?.(remitoId)) {
        total += 1;
        if (statusById[instrId] === "delivered") entregados += 1;
      }
    }
    return { entregados, total };
  }

  // Cargar remitos al abrir/cambiar OS
  useEffect(() => {
    (async () => {
      await loadRemitosExistentes();
    })();
    // eslint-disable-next-line
  }, [os?.id]);

  // Cargar estados de instrumentos al abrir o cambiar la lista
  useEffect(() => {
    if (!open) return;
    loadStatuses();
    // eslint-disable-next-line
  }, [open, instrumentsOfOS]);

  // Cargar items al cambiar el remito activo
  useEffect(() => {
    if (currentRemitoId) refreshItems();
    // eslint-disable-next-line
  }, [currentRemitoId]);

  // Mantener “ya remitados”
  useEffect(() => {
    if (!open) return;
    refreshRemittedMap(remitos);
    // eslint-disable-next-line
  }, [open, remitos.length]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function resetNewRemitoForm() {
    setOfficialNumber("0001-0000");
    setDateOut(getTodayLocalYMD());
    setNotes("");
  }

  useEffect(() => {
    if (!open || editingRemitoId) return;
    resetNewRemitoForm();
    // eslint-disable-next-line
  }, [open, os?.id, editingRemitoId]);

  // --- Crear o guardar edición ---
  const handleCrearOrGuardar = async () => {
    const client_id = os?.client_id;
    try {
      if (!os?.id || !client_id) throw new Error("Falta OS o cliente.");
      setSaving(true);
      const selectedDateOut = dateOut || getTodayLocalYMD();

      // Guardar edición
      if (editingRemitoId) {
        const { error } = await supabase
          .from("remitos")
          .update({
            official_number: officialNumber?.trim() || null,
            code: officialNumber?.trim() || null,
            date_out: ymdToNoonIso(selectedDateOut),
            notes: notes || null,
          })
          .eq("id", editingRemitoId);

        if (error) throw error;

        const rows = await loadRemitosExistentes();
        setCurrentRemitoId(editingRemitoId);
        await refreshItems();
        await refreshRemittedMap(rows);
        try {
          await syncDepartureDateForRemito(editingRemitoId);
        } catch (syncErr) {
          console.warn("[RemitosModal] sync departure date:", syncErr?.message || syncErr);
        }

        setToast({ type: "success", msg: "Remito actualizado." });
        setEditingRemitoId(null);
        return;
      }

      // Crear
      const remitoId = await createRemito({
        os_id: os.id,
        os,
        code: officialNumber?.trim() || null,
        date_out: ymdToNoonIso(selectedDateOut),
        client_id,
        notes,
        official_number: officialNumber?.trim() || null,
      });

      const rows = await loadRemitosExistentes();
      setCurrentRemitoId(remitoId);
      resetNewRemitoForm();
      await refreshItems();
      await refreshRemittedMap(rows);

      setToast({
        type: "success",
        msg: "Remito creado. Agrega instrumentos o usa 'Seleccionar no remitados'.",
      });
    } catch (e) {
      console.error(e);
      setToast({ type: "error", msg: e.message || "Error al guardar remito" });
    } finally {
      setSaving(false);
    }
  };

  function handleStartEdit(remito) {
    if (!remito?.id) return;
    setEditingRemitoId(remito.id);
    setCurrentRemitoId(remito.id);
    setOfficialNumber((remito.official_number || remito.code || "").trim());
    setDateOut(toInputYMD(remito.date_out) || getTodayLocalYMD());
    setNotes(remito.notes || "");
  }

  function handleCancelEdit() {
    setEditingRemitoId(null);
    resetNewRemitoForm();
  }

  // ✅ ELIMINAR remito (real en BD + verificación)
  async function handleDeleteRemito(remito) {
    try {
      if (!remito?.id) return;

      const ok = window.confirm(
        "¿Seguro que querés ELIMINAR este remito?\n\nEsto borrará también sus items."
      );
      if (!ok) return;

      // ✅ borrado real (items + cabecera)
      await deleteRemito(remito.id);

      // ✅ verificación: re-listar y comprobar que no exista más
      const after = await listRemitosByOS(os.id, os);
      const stillThere = after.some((r) => r.id === remito.id);
      if (stillThere) {
        throw new Error(
          "No se pudo eliminar en BD (sigue existiendo). Revisar policies RLS de DELETE en remitos/remito_items."
        );
      }

      setToast({ type: "success", msg: "Remito eliminado." });

      setRemitos(after);
      setEditingRemitoId(null);

      if (currentRemitoId === remito.id) {
        const nextId = after?.[0]?.id || null;
        setCurrentRemitoId(nextId);
        setRemitoItems([]);
      }

      await refreshRemittedMap(after);
    } catch (e) {
      console.error(e);
      setToast({ type: "error", msg: e.message || "No se pudo eliminar" });
    }
  }

  // Agregar 1 (con advertencia si ya fue remitado en otro remito)
  async function handleAdd(instr) {
    if (!currentRemitoId) return;

    const alreadyInThisRemito = remitoItems.some((it) => it.instrument_id === instr.id);
    if (alreadyInThisRemito) return;

    const alreadyRemittedElsewhere =
      remittedMap.has(instr.id) && !remittedMap.get(instr.id).has(currentRemitoId);

    if (alreadyRemittedElsewhere) {
      const ok = window.confirm(
        "Este instrumento ya fue remitado en otro remito de esta OS.\n¿Querés agregarlo igual al remito actual?"
      );
      if (!ok) return;
    }

    await addRemitoItem({
      remito_id: currentRemitoId,
      instrument_id: instr.id,
      qty: 1,
      state: "OK",
      tag: instr.tag,
    });

    await new Promise((resolve) => setTimeout(resolve, 150));
    await refreshItems();
    await refreshRemittedMap();
  }

  // Agregar TODOS los pendientes (no remitados)
  async function handleAddRemaining() {
    if (!currentRemitoId) return;

    const pendings = (instrumentsOfOS || []).filter((i) => {
      const inThis = remitoItems.some((it) => it.instrument_id === i.id);
      if (inThis) return false;
      return !remittedMap.has(i.id);
    });

    const ops = pendings.map((i) =>
      addRemitoItem({
        remito_id: currentRemitoId,
        instrument_id: i.id,
        qty: 1,
        state: "OK",
        tag: i.tag,
      })
    );

    await Promise.allSettled(ops);
    await new Promise((r) => setTimeout(r, 250));
    await refreshItems();
    await refreshRemittedMap();
  }

  // Agregar TODOS (incluye remitados)
  async function handleAddAll() {
    if (!currentRemitoId) return;

    const pendings = (instrumentsOfOS || [])
      .filter((i) => !remitoItems.some((it) => it.instrument_id === i.id))
      .map((i) =>
        addRemitoItem({
          remito_id: currentRemitoId,
          instrument_id: i.id,
          qty: 1,
          state: "OK",
          tag: i.tag,
        })
      );

    await Promise.allSettled(pendings);
    await new Promise((r) => setTimeout(r, 250));
    await refreshItems();
    await refreshRemittedMap();
  }

  async function handleRemove(item) {
    await removeRemitoItem(item.item_id);
    await new Promise((resolve) => setTimeout(resolve, 150));
    await refreshItems();
    await refreshRemittedMap();
  }

  // PDF
  async function handleGeneratePDF(remitoId) {
    if (generatingPDF) return;
    setGeneratingPDF(true);
    try {
      if (remitoId === currentRemitoId) {
        await refreshItems();
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
      if (remitoItems.length === 0) {
        await refreshItems();
        await new Promise((r) => setTimeout(r, 250));
      }
      await printRemitoSoloTexto(remitoId, { ...calib });
    } catch (e) {
      console.error("[RemitosModal] Error generating PDF:", e);
      alert("Error al generar el PDF: " + (e.message || "Error desconocido"));
    } finally {
      setGeneratingPDF(false);
    }
  }

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const activeRemito = remitos.find((r) => r.id === currentRemitoId);

  // Ordenar items por número OS (solo dígitos)
  const itemsSorted = useMemo(() => {
    const arr = [...remitoItems];
    arr.sort((a, b) => {
      const A = parseInt(buildCodigoDigits(a)) || 0;
      const B = parseInt(buildCodigoDigits(b)) || 0;
      return A - B;
    });
    return arr;
  }, [remitoItems]);

  // Pendientes (no remitados nunca)
  const countPendientes = useMemo(() => {
    return (instrumentsOfOS || []).filter((i) => !remittedMap.has(i.id)).length;
  }, [instrumentsOfOS, remittedMap]);

  const instrumentById = useMemo(() => {
    return new Map((instrumentsOfOS || []).map((i) => [i.id, i]));
  }, [instrumentsOfOS]);

  // El formulario de creacion no debe heredar fecha ni numero del remito seleccionado.

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={onBackdrop}
    >
      <div
        className="w-full bg-[#0F1115] text-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ width: "min(95vw, 1400px)", maxHeight: "90vh" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0F1115]/95 backdrop-blur px-7 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Remitos — {os?.code || `OS-${os?.number}`}
          </h2>

          <div className="flex items-center gap-2">
            {activeRemito && (
              <>
                <div className="hidden sm:flex items-center gap-2 mr-2">
                  <label className="text-xs opacity-70">X</label>
                  <input
                    type="number"
                    step="0.5"
                    className="w-16 bg-white/10 rounded px-1 py-0.5 text-sm"
                    value={calib.offsetX}
                    onChange={(e) =>
                      setCalib({ ...calib, offsetX: parseFloat(e.target.value || 0) })
                    }
                    title="Offset X (mm)"
                  />
                  <label className="text-xs opacity-70">Y</label>
                  <input
                    type="number"
                    step="0.5"
                    className="w-16 bg-white/10 rounded px-1 py-0.5 text-sm"
                    value={calib.offsetY}
                    onChange={(e) =>
                      setCalib({ ...calib, offsetY: parseFloat(e.target.value || 0) })
                    }
                    title="Offset Y (mm)"
                  />
                </div>

                <button
                  className="px-4 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleGeneratePDF(activeRemito.id)}
                  disabled={generatingPDF}
                >
                  {generatingPDF ? "Generando..." : "Crear PDF"}
                </button>
              </>
            )}

            <button
              className="px-3 py-1 rounded-lg bg-white text-black hover:bg-gray-200 transition-colors"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-5 p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 64px)" }}
        >
          {/* Panel izquierdo: Crear/Editar remito */}
          <div className="md:col-span-1 space-y-2 rounded-xl bg-white/5 p-4">
            <div className="font-semibold mb-2">{isEditing ? "Editar remito" : "Crear remito"}</div>

            <label className="text-sm opacity-80">N° remito oficial (AFIP)</label>
            <input
              className="w-full bg-white/10 rounded px-2 py-1"
              placeholder="0001-0000xxxx"
              value={officialNumber}
              onChange={(e) => setOfficialNumber(e.target.value)}
            />

            <label className="text-sm opacity-80">Fecha</label>
            <input
              type="date"
              className="w-full bg-white/10 rounded px-2 py-1"
              value={dateOut}
              onChange={(e) => setDateOut(e.target.value)}
            />

            <label className="text-sm opacity-80">Notas</label>
            <textarea
              className="w-full bg-white/10 rounded px-2 py-1"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <button
              disabled={saving || !os?.id}
              className="w-full mt-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={handleCrearOrGuardar}
            >
              {saving ? (isEditing ? "Guardando..." : "Creando...") : isEditing ? "Guardar cambios" : "Crear remito"}
            </button>

            {isEditing && (
              <button
                className="w-full px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 transition-colors"
                onClick={handleCancelEdit}
              >
                Cancelar edición
              </button>
            )}

            <div className="mt-4">
              <div className="font-semibold mb-1">Remitos existentes</div>
              <div className="space-y-2">
                {remitos.map((r) => {
                  const selected = currentRemitoId === r.id;
                  const ds = remitoDeliveryState(r.id);
                  const allDelivered = ds.total > 0 && ds.entregados === ds.total;
                  return (
                    <div
                      key={r.id}
                      className={`w-full px-3 py-2 rounded-lg ${
                        selected ? "bg-white text-black" : "bg-white/10 hover:bg-white/15"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => {
                            setCurrentRemitoId(r.id);
                            setEditingRemitoId(null);
                          }}
                          className="flex-1 text-left"
                          title="Seleccionar remito"
                        >
                          {(r.official_number || r.code)} • {formatEsDate(r.date_out)}
                        </button>

                        <div className="flex items-center gap-2">
                          <button
                            className={`px-3 py-1 rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              selected
                                ? "bg-orange-500 text-white hover:bg-orange-600"
                                : "bg-white/20 hover:bg-white/30 text-white"
                            }`}
                            onClick={() => handleGeneratePDF(r.id)}
                            disabled={generatingPDF}
                            title="Generar PDF"
                          >
                            {generatingPDF ? "..." : "Crear PDF"}
                          </button>

                          <button
                            className={`px-3 py-1 rounded transition-colors ${
                              selected ? "bg-black/10 hover:bg-black/20 text-black" : "bg-white/10 hover:bg-white/15 text-white"
                            }`}
                            onClick={() => handleStartEdit(r)}
                            title="Editar remito"
                          >
                            Editar
                          </button>

                          {canDeleteRemitos && (
                            <button
                              className="px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
                              onClick={() => handleDeleteRemito(r)}
                              title="Eliminar remito"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Línea de entrega (confirmación de que el cliente lo recibió) */}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {ds.total === 0 ? (
                          <span className={`text-xs ${selected ? "text-black/40" : "text-white/35"}`}>
                            Sin instrumentos
                          </span>
                        ) : allDelivered ? (
                          <span className="text-xs font-medium text-emerald-500">
                            ✓ Entregado ({ds.entregados}/{ds.total})
                          </span>
                        ) : (
                          <span className={`text-xs ${selected ? "text-black/60" : "text-white/55"}`}>
                            {ds.entregados > 0
                              ? `Entrega parcial (${ds.entregados}/${ds.total})`
                              : "Pendiente de entrega"}
                          </span>
                        )}

                        {!allDelivered && ds.total > 0 && (
                          <button
                            className="px-3 py-1 rounded text-xs font-medium border border-emerald-500/50 bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                            onClick={() => confirmRemitoDelivery(r)}
                            disabled={confirmingDelivery}
                            title="Marcar como entregado (el cliente ya lo recibió)"
                          >
                            {confirmingDelivery ? "..." : "✓ Confirmar entrega"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {remitos.length === 0 && (
                  <div className="opacity-70 text-sm">No hay remitos aún.</div>
                )}
              </div>
            </div>
          </div>

          {/* Panel central: Instrumentos de la OS */}
          <div className="md:col-span-1 rounded-xl bg-white/5 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">
                Instrumentos de la OS
                {typeof countPendientes === "number" && (
                  <span className="ml-2 text-xs opacity-70">— {countPendientes} pendientes</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  className={`px-3 py-1 rounded transition-colors ${
                    !currentRemitoId ? "bg-white/5 text-white/50 cursor-not-allowed" : "bg-white/10 hover:bg-white/15"
                  }`}
                  onClick={handleAddRemaining}
                  disabled={!currentRemitoId}
                  title="Agrega solo los instrumentos que nunca fueron remitados"
                >
                  Seleccionar no remitados
                </button>
                <button
                  className={`px-3 py-1 rounded transition-colors ${
                    !currentRemitoId ? "bg-white/5 text-white/50 cursor-not-allowed" : "bg-white/10 hover:bg-white/15"
                  }`}
                  onClick={handleAddAll}
                  disabled={!currentRemitoId}
                >
                  Seleccionar todos
                </button>
              </div>
            </div>

            <ul className="space-y-2">
              {instrumentsOfOS.map((i) => {
                const inThisRemito = remitoItems.some((it) => it.instrument_id === i.id);
                const remittedElsewhere =
                  remittedMap.has(i.id) && !(remittedMap.get(i.id)?.has?.(currentRemitoId));

                const codeDigits = onlyDigits(i.os_code || i.code);
                const categoryName = getCategoryName(i);

                return (
                  <li
                    key={i.id}
                    className="flex items-center justify-between bg-white/5 rounded px-3 py-2"
                  >
                    <div className="text-sm">
                      <div className="font-medium">
                        {codeDigits ? `#${codeDigits} — ` : ""}
                        {i.tag}
                        {categoryName ? (
                          <span className="ml-2 text-xs opacity-70">({categoryName})</span>
                        ) : null}
                      </div>
                      <div className="opacity-70">
                        {[i.brand, i.model].filter(Boolean).join(" ")}
                        {i.serial ? ` · S/N ${i.serial}` : ""}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {inThisRemito && (
                        <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-xs">
                          Agregado
                        </span>
                      )}
                      {!inThisRemito && remittedElsewhere && (
                        <span
                          className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 text-xs"
                          title="Ya remitado en otro remito"
                        >
                          Remitado
                        </span>
                      )}

                      <button
                        className={`px-3 py-1 rounded transition-colors ${
                          inThisRemito || !currentRemitoId
                            ? "bg-white/5 text-white/50 cursor-not-allowed"
                            : "bg-white/10 hover:bg-white/15"
                        }`}
                        onClick={() => handleAdd(i)}
                        disabled={inThisRemito || !currentRemitoId}
                        title={
                          remittedElsewhere
                            ? "Este instrumento ya fue remitado en otro remito"
                            : "Agregar al remito actual"
                        }
                      >
                        {inThisRemito ? "Agregado" : "Agregar"}
                      </button>
                    </div>
                  </li>
                );
              })}

              {instrumentsOfOS.length === 0 && (
                <div className="opacity-70 text-sm">Sin instrumentos ingresados.</div>
              )}
            </ul>
          </div>

          {/* Panel derecho: Items del remito seleccionado */}
          <div className="md:col-span-1 rounded-xl bg-white/5 p-4">
            <div className="font-semibold mb-2">
              Remito: {(activeRemito?.official_number || activeRemito?.code) ?? "—"}
            </div>

            <ul className="space-y-2">
              {itemsSorted.map((it) => {
                const codeDigits = buildCodigoDigits(it);
                const instr = instrumentById.get(it.instrument_id);
                const label = instr ? buildInstrumentLabel(instr) : it.instr_label;

                const st = statusById[it.instrument_id];
                const isDelivered = st === "delivered";
                const isRejected = st === "rejected";
                return (
                  <li
                    key={it.item_id}
                    className="flex items-center justify-between bg-white/5 rounded px-3 py-2"
                  >
                    <div className="text-sm">
                      <div className="font-medium">
                        #{codeDigits} — {it.tag ?? it.instr_tag}
                      </div>
                      <div className="opacity-70">{label}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isRejected ? (
                        <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 text-xs">
                          Rechazado
                        </span>
                      ) : isDelivered ? (
                        <button
                          className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 text-xs hover:bg-emerald-500/30 transition-colors"
                          onClick={() => toggleInstrumentDelivery(it.instrument_id, false)}
                          title="Deshacer entrega (vuelve a remitido)"
                        >
                          ✓ Entregado
                        </button>
                      ) : (
                        <button
                          className="px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/15 transition-colors"
                          onClick={() => toggleInstrumentDelivery(it.instrument_id, true)}
                          title="Confirmar entrega de este instrumento"
                        >
                          Entregar
                        </button>
                      )}
                      <button
                        className="px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
                        onClick={() => handleRemove(it)}
                      >
                        Quitar
                      </button>
                    </div>
                  </li>
                );
              })}

              {itemsSorted.length === 0 && (
                <div className="opacity-70 text-sm">Sin items en este remito.</div>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
